import {getResource, createResource, updateResource} from "./common"
import {JWTProviders} from "./orgSettings"
import {put} from "../utilities/http"
import {base64ToBlob} from "../utilities/file"

export type WhiteLabelTheme = {
	type: "whiteLabelTheme"
	id: string
	attributes: {
		createdAt: Date
		fileName: string
		updatedAt: Date
		name: string
	}
	links: {
		related: {
			type: "application/json"
			href: string
		}
	}
}

export type WhiteLabelLanguage = {
	type: "whiteLabelLanguage"
	id: string
	attributes: {
		createdAt: Date
		fileName: string
		updatedAt: Date
	}
	links: {
		related: {
			type: "application/json"
			href: string
		}
	}
}

export type AppConfig = {
	type: "whiteLabelAppConfig"
	id: string
	attributes: {
		jwtIssuer?: string
		jwtProvider?: {
			provider: JWTProviders | null
			jwksPath: string | null
		}
		jwtPublicKey?: string | null
		applicationFormVersion: string
		endUserConfigUrl?: string
		findEndUsersUrl?: string
		bankingPageURL?: string
		capitalPageURL?: string
		billpayPageURL?: string
	}
	relationships: {
		org: {
			data: {
				type: "org"
				id: string
			}
		}
		webhook: {
			data: {
				type: "webhook"
				id: string
			}
		}
	}
}

export type WhiteLabelAppUser = {
	type: "whiteLabelAppUser"
	id: string
	attributes: {
		createdAt: string
		externalUserId: string
		userRole: "Admin" | "ReadOnly" | string
		status: string
		email?: string
		phone?: string
		fullName?: {
			first: string
			last: string
		}
	}
	relationships: {
		applicationForm?: {
			data: {
				type: "applicationForm"
				id: string
			}
		}
		org: {
			data: {
				type: "org"
				id: string
			}
		}
		customer?: {
			data: {
				type: "customer"
				id: string
			}
		}
	}
}

export async function getThemes(accessToken: string) {
	const query = {
		page: {
			limit: 10000,
			offset: 0,
		},
	}

	const result = await getResource<WhiteLabelTheme[]>("white-label/theme", accessToken, query)
	return result.map((v) => v.data)
}

export async function getLanguages(accessToken: string) {
	const query = {
		page: {
			limit: 10000,
			offset: 0,
		},
	}

	const result = await getResource<WhiteLabelLanguage[]>("white-label/language", accessToken, query)
	return result.map((v) => v.data)
}

export async function getDefaultTheme(accessToken: string) {
	const result = await getResource<WhiteLabelTheme>("white-label/theme/default", accessToken)
	return result.map((v) => v.data)
}

export async function createTheme(accessToken: string, attributes: any) {
	const body = {
		type: "whiteLabelTheme",
		attributes,
	}

	return await createResource<WhiteLabelTheme>("white-label/theme", accessToken, body)
}

export async function getAppConfig(accessToken: string) {
	const result = await getResource<AppConfig>("white-label/app/configs", accessToken)
	return result.map((v) => v.data)
}

export async function getAppConfigByOrgId(accessToken: string, orgId: string) {
	const query = {
		filter: {
			orgId,
		},
	}
	const result = await getResource<AppConfig>("white-label/app/configs", accessToken, query)
	return result.map((v) => v.data)
}

export async function getWhiteLabelAppUsersByOrgId(accessToken: string, orgId: string) {
	const query = {
		filter: {
			orgId,
		},
		page: {
			limit: 10000,
			offset: 0,
		},
	}
	const result = await getResource<WhiteLabelAppUser[]>("white-label/app/users", accessToken, query)
	return result.map((v) => v.data)
}

export async function updateAppConfig(
	accessToken: string,
	attributes: Partial<AppConfig["attributes"]>,
	orgId?: string
) {
	const result = await updateResource<AppConfig>("white-label/app/configs", accessToken, {
		type: "whiteLabelAppConfig",
		attributes,
		relationships: orgId
			? {
					org: {
						data: {
							type: "org",
							id: orgId,
						},
					},
			  }
			: undefined,
	})

	return result.map((v) => v.data)
}

export async function patchAppConfig(accessToken: string, attributes: Partial<AppConfig["attributes"]>, orgId: string) {
	const result = await updateResource<AppConfig>("white-label/app/configs", accessToken, {
		type: "whiteLabelAppConfig",
		attributes,
		relationships: {
			org: {
				data: {
					type: "org",
					id: orgId,
				},
			},
		},
	})

	return result.map((v) => v.data)
}

export async function createAppConfig(
	accessToken: string,
	attributes: Partial<AppConfig["attributes"]> & {jwtIssuer: string},
	orgId: string
) {
	const result = await createResource<AppConfig>("white-label/app/configs", accessToken, {
		type: "whiteLabelAppConfig",
		attributes,
		relationships: {
			org: {
				data: {
					type: "org",
					id: orgId,
				},
			},
		},
	})

	return result.map((v) => v.data)
}

export type WhiteLabelAssetUploadLink = {
	type: "whiteLabelAssetUploadLink"
	attributes: {
		uploadLink: string
		downloadLink: string
	}
}

export async function createWhiteLabelAssetUploadLink(accessToken: string) {
	return await createResource<WhiteLabelAssetUploadLink>("white-label/theme/assets", accessToken, {})
}

export async function uploadWhiteLabelAsset(uploadLink: string, base64File: string) {
	// Use the utility function to convert base64 to blob and get content type
	const {blob, contentType} = base64ToBlob(base64File)

	const headers = {
		"Content-Type": contentType,
	}

	return await put(uploadLink, blob, {headers, withCredentials: false})
}
